/**
 * Frontend API client for the Import Website feature. Mirrors the SSE
 * pattern used by `roary.ts` but is independent — it imports nothing from
 * the existing roary module and uses event names prefixed with `import.`
 * so the two streams can never collide.
 */

import apiClient, { API_BASE_URL, tokenManager } from '../core';

export type ImportEventType =
  | 'connected'
  | 'done'
  | 'import.scan_started'
  | 'import.page_discovered'
  | 'import.page_parsed'
  | 'import.page_failed'
  | 'import.asset_discovered'
  | 'import.brand_extracted'
  | 'import.scan_summary'
  | 'import.analysis_started'
  | 'import.plan_ready'
  | 'import.build_started'
  | 'import.asset_hosted'
  | 'import.asset_failed'
  | 'import.brand_package_created'
  | 'import.site_created'
  | 'import.layout_created'
  | 'import.build_page_started'
  | 'import.build_page_done'
  | 'import.build_completed'
  | 'import.completed'
  | 'import.failed';

export interface ImportEvent {
  type: ImportEventType;
  data: Record<string, unknown>;
}

export interface ProposedComponent {
  type: string;
  data: Record<string, unknown>;
  sourcePageIds?: string[];
  sourceAssetIds?: string[];
}

export interface ProposedPage {
  title: string;
  slug: string;
  isHomePage: boolean;
  sourcePageId: string | null;
  components: ProposedComponent[];
}

export interface ProposedPlan {
  brand: {
    brandName?: string;
    siteName?: string;
    colors: Partial<{
      primary: string;
      secondary: string;
      accent: string;
      background: string;
      text: string;
    }>;
    fonts: Partial<{ headingFont: string; bodyFont: string }>;
    logoAssetId?: string | null;
  };
  pages: ProposedPage[];
  warnings?: string[];
}

export interface ImportSessionDetails {
  id: string;
  sourceUrl: string;
  sourceOrigin: string;
  status: string;
  marshmallowsCharged: number;
  pagesTarget: number;
  depthTarget: number;
  brandColors: Record<string, unknown> | null;
  brandFonts: Record<string, unknown> | null;
  logoUrl: string | null;
  proposedPlan: ProposedPlan | null;
  builtSiteId: string | null;
  layoutTemplateId?: string | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface CreateSessionResponse {
  sessionId: string;
  sourceUrl: string;
  sourceOrigin: string;
  status: string;
  pagesTarget: number;
  depthTarget: number;
  marshmallowsCharged: number;
  marshmallowsRemaining: number;
  layoutTemplateId?: string | null;
}

export const roaryImportApi = {
  async createSession(
    url: string,
    options: { templateId: string }
  ): Promise<CreateSessionResponse> {
    const response = await apiClient.post<CreateSessionResponse>('/roary/import/sessions', {
      url,
      templateId: options.templateId,
    });
    return response.data;
  },

  async getSession(sessionId: string): Promise<{
    session: ImportSessionDetails;
    pages: Array<Record<string, unknown>>;
    assets: Array<Record<string, unknown>>;
  }> {
    const response = await apiClient.get<{
      session: ImportSessionDetails;
      pages: Array<Record<string, unknown>>;
      assets: Array<Record<string, unknown>>;
    }>(`/roary/import/sessions/${sessionId}`);
    return response.data;
  },

  async listSessions(): Promise<{ sessions: ImportSessionDetails[] }> {
    const response = await apiClient.get<{ sessions: ImportSessionDetails[] }>(
      '/roary/import/sessions'
    );
    return response.data;
  },

  async cancelSession(sessionId: string): Promise<{ ok: boolean; refunded?: boolean }> {
    const response = await apiClient.post<{ ok: boolean; refunded?: boolean }>(
      `/roary/import/sessions/${sessionId}/cancel`
    );
    return response.data;
  },

  /**
   * Open an SSE stream and run the scan + analysis. Returns an abort
   * function that closes the stream early.
   */
  startScan(
    sessionId: string,
    onEvent: (event: ImportEvent) => void,
    body?: { paths?: string[] }
  ): { abort: () => void } {
    return openSSE(
      `/roary/import/sessions/${sessionId}/scan`,
      body ? JSON.stringify(body) : undefined,
      onEvent
    );
  },

  /**
   * Open an SSE stream and run the build using the (optionally edited) plan.
   */
  startBuild(
    sessionId: string,
    planOverride: ProposedPlan,
    onEvent: (event: ImportEvent) => void
  ): { abort: () => void } {
    return openSSE(
      `/roary/import/sessions/${sessionId}/build`,
      JSON.stringify({ planOverride }),
      onEvent
    );
  },
};

function openSSE(
  path: string,
  body: string | undefined,
  onEvent: (event: ImportEvent) => void
): { abort: () => void } {
  const controller = new AbortController();
  const url = `${API_BASE_URL}${path}`;
  const token = tokenManager.get();

  (async () => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'text/event-stream',
        },
        body: body ?? '{}',
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        const text = await response.text().catch(() => '');
        onEvent({
          type: 'import.failed',
          data: { message: `HTTP ${response.status}`, body: text },
        });
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split('\n\n');
        buffer = messages.pop() ?? '';
        for (const msg of messages) {
          const lines = msg.split('\n');
          let event = 'message';
          const dataParts: string[] = [];
          for (const line of lines) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataParts.push(line.slice(5).trim());
          }
          if (dataParts.length === 0) continue;
          let data: Record<string, unknown> = {};
          try {
            data = JSON.parse(dataParts.join('\n'));
          } catch {
            data = { raw: dataParts.join('\n') };
          }
          onEvent({ type: event as ImportEventType, data });
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      onEvent({
        type: 'import.failed',
        data: { message: err instanceof Error ? err.message : String(err) },
      });
    }
  })();

  return {
    abort: () => controller.abort(),
  };
}
